import { useState, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import endpoints, { api } from '../apiConfig';
import styles from './RoleManagement.module.css';

const RoleManagement = ({ onRoleUpdate }) => {
  const [currentRole, setCurrentRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await api.get(endpoints.user.profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(response.data);
      setCurrentRole(response.data.role);
    } catch (err) {
      setError('Failed to fetch user data');
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (newRole) => {
    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();
      await api.put(endpoints.user.updateRole, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCurrentRole(newRole);
      setShowModal(false);
      if (onRoleUpdate) {
        onRoleUpdate();
      }
    } catch (err) {
      setError('Failed to update role');
      console.error('Error updating role:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'collector':
        return 'As an Art Collector, you can discover and purchase unique artworks from emerging and established artists.';
      case 'artist':
        return 'As an Artist, you can showcase and sell your artwork to a global audience of collectors and galleries.';
      case 'gallery':
        return 'As a Gallery, you can represent artists and create virtual exhibitions for art enthusiasts.';
      case 'curator':
        return 'As a Curator, you can build curated collections and earn fees while engaging with the community.';
      default:
        return '';
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className={styles.roleManagement}>
      <div className={styles.roleSection}>
        <div className={styles.roleHeader}>
          <h2>Your Role</h2>
          <div className={`${styles.roleBadge} ${!currentRole ? styles.roleBadgeEmpty : ''}`}>
            {currentRole ? currentRole.charAt(0).toUpperCase() + currentRole.slice(1) : 'No Role Selected'}
          </div>
        </div>

        {currentRole && (
          <p className={styles.roleDescription}>
            {getRoleDescription(currentRole)}
          </p>
        )}

        <button 
          className={styles.primaryButton}
          onClick={() => setShowModal(true)}
        >
          {currentRole ? 'Change Role' : 'Select Role'}
        </button>

        {showModal && (
          <div className={styles.roleSelectorModal}>
            <div className={styles.roleSelectorContent}>
              <h3>Select Your Role</h3>
              <div className={styles.roleOptions}>
                {['collector', 'artist', 'gallery', 'curator'].map((role) => (
                  <button
                    key={role}
                    className={`${styles.roleOptionButton} ${currentRole === role ? styles.selected : ''}`}
                    onClick={() => updateRole(role)}
                  >
                    <span className={styles.roleTitle}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </span>
                    <span className={styles.roleDescription}>
                      {getRoleDescription(role)}
                    </span>
                  </button>
                ))}
              </div>
              <button 
                className={styles.secondaryButton}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement; 